import { Currency, localProjectIdMapper, ProjectMix, localCarbonRemovalProjectIdMapper } from '..';
import { ImpactType, ProjectType } from '@prisma/client';
import type {
  AdminCarbonAvoidanceProjectType,
  AdminCarbonRemovalProjectType,
  AdminHabitatRestorationProjectType,
} from '../projects';

export type CurrencyCostMapper = Record<Currency, number>;

export type ProjectForPricing =
  | {
      id: number;
      impactType: typeof ImpactType.TREES;
    }
  | {
      impactType: typeof ImpactType.CARBON_REMOVAL;
      projectType: AdminCarbonRemovalProjectType;
      id?: number;
    }
  | {
      impactType: typeof ImpactType.CARBON_REMOVAL;
      id: number;
      projectType?: never;
    }
  | {
      impactType: typeof ImpactType.CARBON_OFFSETS;
      projectType: AdminCarbonAvoidanceProjectType;
    }
  | {
      impactType: typeof ImpactType.HABITAT_RESTORATION;
      projectType: AdminHabitatRestorationProjectType;
    };

type ProjectPricingFields = {
  id: number;
  impactType: ImpactType | `${ImpactType}`;
  projectType: ProjectType;
};

export const toProjectForPricing = (project: ProjectPricingFields): ProjectForPricing => {
  const impactType = project.impactType as ImpactType;

  if (impactType === ImpactType.TREES) {
    return { id: project.id, impactType };
  }

  return {
    id: project.id,
    impactType,
    projectType: project.projectType,
  } as ProjectForPricing;
};

type CarbonAvoidanceProjectForPricing = Extract<ProjectForPricing, { impactType: typeof ImpactType.CARBON_OFFSETS }>;
type CarbonRemovalProjectForPricing = Extract<ProjectForPricing, { impactType: typeof ImpactType.CARBON_REMOVAL }>;
type HabitatRestorationProjectForPricing = Extract<
  ProjectForPricing,
  { impactType: typeof ImpactType.HABITAT_RESTORATION }
>;

const NO_PRICE_ERROR_TEXT = 'No price exists for project type';

const getCarbonAvoidanceProjectPrice = (project: CarbonAvoidanceProjectForPricing, currency: Currency): number => {
  switch (project.projectType) {
    case ProjectType.FORESTRY:
      return customAvoidanceCosts[ProjectType.FORESTRY][currency];
    case ProjectType.CAPTURE:
      return customAvoidanceCosts[ProjectType.CAPTURE][currency];
    case ProjectType.WETLANDS:
      return customAvoidanceCosts[ProjectType.WETLANDS][currency];
    case ProjectType.RENEWABLES:
      return customAvoidanceCosts[ProjectType.RENEWABLES][currency];
    case ProjectType.COMMUNITY:
      return customAvoidanceCosts[ProjectType.COMMUNITY][currency];
    case ProjectType.TRANSPORT:
      return customAvoidanceCosts[ProjectType.TRANSPORT][currency];
    case ProjectType.GEOTHERMAL:
      return customAvoidanceCosts[ProjectType.GEOTHERMAL][currency];
    case ProjectType.HOUSEHOLD_RETROFIT:
      return customAvoidanceCosts[ProjectType.HOUSEHOLD_RETROFIT][currency];
    default:
      throw new Error(`${NO_PRICE_ERROR_TEXT} ${project.projectType}`);
  }
};

const getCarbonRemovalProjectPrice = (project: CarbonRemovalProjectForPricing, currency: Currency): number => {
  const localPrice =
    project.id !== undefined ? localCarbonRemovalProjectIdMapper[project.id]?.prices[currency] : undefined;
  if (localPrice) return localPrice;

  if (!('projectType' in project) || !project.projectType) {
    throw new Error(`${NO_PRICE_ERROR_TEXT} for local project ${project.id}`);
  }

  const { projectType } = project;

  switch (projectType) {
    case ProjectType.AFFORESTATION_REFORESTATION_AND_REVEGETATION:
      return customRemovalCosts[ProjectType.AFFORESTATION_REFORESTATION_AND_REVEGETATION][currency];
    case ProjectType.BIOCHAR:
      return customRemovalCosts[ProjectType.BIOCHAR][currency];
    case ProjectType.BLUE_CARBON:
      return customRemovalCosts[ProjectType.BLUE_CARBON][currency];
    case ProjectType.DIRECT_AIR_CAPTURE:
      return customRemovalCosts[ProjectType.DIRECT_AIR_CAPTURE][currency];
    case ProjectType.ENHANCED_ROCK_WEATHERING:
      return customRemovalCosts[ProjectType.ENHANCED_ROCK_WEATHERING][currency];
    case ProjectType.REGENERATIVE_AGRICULTURE:
      return customRemovalCosts[ProjectType.REGENERATIVE_AGRICULTURE][currency];
    default:
      throw new Error(NO_PRICE_ERROR_TEXT);
  }
};

const getHabitatRestorationProjectPrice = (
  project: HabitatRestorationProjectForPricing,
  currency: Currency,
): number => {
  switch (project.projectType) {
    case ProjectType.WILDFLOWERS:
      return customRestorationCosts[ProjectType.WILDFLOWERS][currency];
    case ProjectType.SEAGRASS:
      return customRestorationCosts[ProjectType.SEAGRASS][currency];
    case ProjectType.PEATLANDS:
      return customRestorationCosts[ProjectType.PEATLANDS][currency];
    case ProjectType.RAINFOREST_PROTECTION:
      return customRestorationCosts[ProjectType.RAINFOREST_PROTECTION][currency];
    case ProjectType.MARINE_PROTECTION_RESTORATION:
      return customRestorationCosts[ProjectType.MARINE_PROTECTION_RESTORATION][currency];
    default:
      throw new Error(`${NO_PRICE_ERROR_TEXT} ${project.projectType}`);
  }
};

export const getProjectPrice = (project: ProjectForPricing, currency: Currency): number => {
  if (project.impactType === ImpactType.CARBON_OFFSETS) {
    return getCarbonAvoidanceProjectPrice(project, currency);
  }

  if (project.impactType === ImpactType.CARBON_REMOVAL) {
    return getCarbonRemovalProjectPrice(project, currency);
  }

  if (project.impactType === ImpactType.HABITAT_RESTORATION) {
    return getHabitatRestorationProjectPrice(project, currency);
  }

  if (project.impactType === ImpactType.TREES) {
    return localProjectIdMapper[project.id]?.prices[currency] || customTreesCosts[currency];
  }

  throw new Error('Unsupported project encountered when determining pricing');
};

export const landscapeRestorationTreeCosts: CurrencyCostMapper = {
  GBP: 2,
  EUR: 2.4,
  USD: 2.8,
  CAD: 3.8,
  AUD: 4,
};

export const getProjectMixPrice = (projectMix: ProjectMix, currency: Currency): number => {
  if (projectMix === ProjectMix.GLOBAL_TREES || projectMix === ProjectMix.CHEAP_AGROFORESTRY) {
    return customTreesCosts[currency];
  }

  if (projectMix === ProjectMix.LANDSCAPE_RESTORATION) {
    return landscapeRestorationTreeCosts[currency];
  }

  throw new Error('Unsupported project mix encountered when determining pricing');
};

export const customTreesCosts: CurrencyCostMapper = {
  GBP: 0.6,
  EUR: 0.8,
  USD: 0.89,
  CAD: 1.24,
  AUD: 1.34,
};

export const SHOPIFY_TREE_PRICE_CENTS = 80;
export const SHOPIFY_CARBON_REMOVAL_KG_PRICE_CENTS = 28;

/** Legacy mapper, used for subscriptions and "donations" (among other areas) */
export const customOffsetsCosts: CurrencyCostMapper = {
  GBP: 9.45,
  EUR: 11.2,
  USD: 12.5,
  CAD: 16.4,
  AUD: 18.9,
};

/**
 * Each 'project type' maps to one of four pricing structures (which for simplicity
 * aren't represented here).
 *
 * See [this documentation](https://www.notion.so/ecologi-action/Default-unit-prices-are-autofilled-for-each-line-item-f9c5c42fd351461095e4de5adc28ed6a?pvs=4#bafbfbd2bcb94018ae7f0e0e32ea529d) for the mapping.
 */
export const customAvoidanceCosts: Record<AdminCarbonAvoidanceProjectType, CurrencyCostMapper> = {
  [ProjectType.CAPTURE]: {
    GBP: 6.9,
    EUR: 8.7,
    USD: 10.18,
    CAD: 14.22,
    AUD: 15.46,
  },
  [ProjectType.COMMUNITY]: {
    GBP: 7.95,
    EUR: 10.5,
    USD: 12,
    CAD: 16.5,
    AUD: 17.5,
  },
  [ProjectType.HOUSEHOLD_RETROFIT]: {
    GBP: 90,
    EUR: 114.75,
    USD: 132.3,
    CAD: 184.95,
    AUD: 189.09,
  },
  [ProjectType.FORESTRY]: {
    GBP: 7.9,
    EUR: 9.96,
    USD: 11.66,
    CAD: 16.28,
    AUD: 17.7,
  },
  [ProjectType.GEOTHERMAL]: {
    GBP: 9.45,
    EUR: 11.36,
    USD: 12.69,
    CAD: 17.1,
    AUD: 18.93,
  },
  [ProjectType.RENEWABLES]: {
    GBP: 7.7,
    EUR: 9.1,
    USD: 10.2,
    CAD: 13.4,
    AUD: 15.4,
  },
  [ProjectType.TRANSPORT]: {
    GBP: 9.45,
    EUR: 11.36,
    USD: 12.69,
    CAD: 17.1,
    AUD: 18.93,
  },
  [ProjectType.WETLANDS]: {
    GBP: 7.3,
    EUR: 8.78,
    USD: 9.8,
    CAD: 14.62,
    AUD: 13.21,
  },
};

export const customRemovalCosts: Record<AdminCarbonRemovalProjectType, CurrencyCostMapper> = {
  [ProjectType.AFFORESTATION_REFORESTATION_AND_REVEGETATION]: {
    GBP: 37,
    EUR: 46.63,
    USD: 54.61,
    CAD: 76.25,
    AUD: 82.89,
  },
  [ProjectType.BLUE_CARBON]: {
    GBP: 40,
    EUR: 51,
    USD: 59,
    CAD: 82,
    AUD: 88,
  },
  [ProjectType.BIOCHAR]: {
    GBP: 150,
    EUR: 190.5,
    USD: 221.5,
    CAD: 307.5,
    AUD: 330,
  },
  [ProjectType.DIRECT_AIR_CAPTURE]: {
    GBP: 1000,
    EUR: 1000,
    USD: 1200,
    CAD: 1500,
    AUD: 1590,
  },
  [ProjectType.ENHANCED_ROCK_WEATHERING]: {
    GBP: 245,
    EUR: 292.11,
    USD: 318.4,
    CAD: 435.14,
    AUD: 479.56,
  },
  [ProjectType.REGENERATIVE_AGRICULTURE]: {
    GBP: 44,
    USD: 65,
    EUR: 56,
    AUD: 97,
    CAD: 90.5,
  },
};

export const customRestorationCosts: Record<AdminHabitatRestorationProjectType, CurrencyCostMapper> = {
  [ProjectType.WILDFLOWERS]: {
    GBP: 5.55,
    EUR: 7,
    USD: 8.05,
    CAD: 11.2,
    AUD: 11.75,
  },
  [ProjectType.SEAGRASS]: {
    GBP: 125,
    EUR: 157.53,
    USD: 184,
    CAD: 257.59,
    AUD: 280.03,
  },
  [ProjectType.PEATLANDS]: {
    GBP: 5.5,
    EUR: 6.2,
    USD: 7.4,
    CAD: 10.2,
    AUD: 11.1,
  },
  [ProjectType.RAINFOREST_PROTECTION]: {
    GBP: 0.00079,
    EUR: 0.000996,
    USD: 0.001146,
    CAD: 0.001599,
    AUD: 0.001673,
  },
  [ProjectType.MARINE_PROTECTION_RESTORATION]: {
    GBP: 0.000494,
    EUR: 0.000623,
    USD: 0.000717,
    CAD: 0.000999,
    AUD: 0.001045,
  },
};
